import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaShoppingCart, FaBriefcase, FaBox, FaMoneyBillWave, FaMobileAlt, FaLock, FaSync, FaChartLine, FaGlobe, FaBullhorn, FaEnvelope } from 'react-icons/fa';
import theme from '../../theme';

const FeaturesSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.boxShadow};
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.6;
`;

const CategorySection = styled.div`
  margin-bottom: 4rem;
`;

const CategoryTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturesPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const platformFeatures = [
    {
      icon: <FaShoppingCart />,
      titleKey: 'featuresPage.platform.items.orderManagement.title',
      descKey: 'featuresPage.platform.items.orderManagement.description'
    },
    {
      icon: <FaBriefcase />,
      titleKey: 'featuresPage.platform.items.customerManagement.title',
      descKey: 'featuresPage.platform.items.customerManagement.description'
    },
    {
      icon: <FaBox />,
      titleKey: 'featuresPage.platform.items.stockManagement.title',
      descKey: 'featuresPage.platform.items.stockManagement.description'
    },
    {
      icon: <FaMoneyBillWave />,
      titleKey: 'featuresPage.platform.items.dynamicPricing.title',
      descKey: 'featuresPage.platform.items.dynamicPricing.description'
    },
    {
      icon: <FaMobileAlt />,
      titleKey: 'featuresPage.platform.items.responsive.title',
      descKey: 'featuresPage.platform.items.responsive.description'
    },
    {
      icon: <FaLock />,
      titleKey: 'featuresPage.platform.items.security.title',
      descKey: 'featuresPage.platform.items.security.description'
    }
  ];
  
  const erpFeatures = [
    {
      icon: <FaSync />,
      titleKey: 'featuresPage.integration.items.erp.title',
      descKey: 'featuresPage.integration.items.erp.description'
    },
    {
      icon: <FaChartLine />,
      titleKey: 'featuresPage.integration.items.reporting.title',
      descKey: 'featuresPage.integration.items.reporting.description'
    },
    {
      icon: <FaGlobe />,
      titleKey: 'featuresPage.integration.items.multiLang.title',
      descKey: 'featuresPage.integration.items.multiLang.description'
    }
  ];
  
  const marketingFeatures = [
    {
      icon: <FaBullhorn />,
      titleKey: 'featuresPage.marketing.items.campaigns.title',
      descKey: 'featuresPage.marketing.items.campaigns.description'
    },
    {
      icon: <FaEnvelope />,
      titleKey: 'featuresPage.marketing.items.email.title',
      descKey: 'featuresPage.marketing.items.email.description'
    },
    {
      icon: <FaMobileAlt />,
      titleKey: 'featuresPage.marketing.items.mobileApp.title',
      descKey: 'featuresPage.marketing.items.mobileApp.description'
    }
  ];
  
  return (
    <>
      <SEO titleKey="featuresPage.seo.title" descriptionKey="featuresPage.seo.description" />
      
      <FeaturesSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('featuresPage.hero.title')}</PageTitle>
            <PageSubtitle>
              {t('featuresPage.hero.subtitle')}
            </PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            <CategorySection>
              <CategoryTitle>{t('featuresPage.platform.title')}</CategoryTitle>
              <p>{t('featuresPage.platform.description')}</p>
              
              <FeatureGrid>
                {platformFeatures.map((feature, index) => (
                  <FeatureCard key={index}>
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
                    <FeatureDescription>{t(feature.descKey)}</FeatureDescription>
                  </FeatureCard>
                ))}
              </FeatureGrid>
            </CategorySection>
            
            <CategorySection>
              <CategoryTitle>{t('featuresPage.integration.title')}</CategoryTitle>
              <p>{t('featuresPage.integration.description')}</p>
              
              <FeatureGrid>
                {erpFeatures.map((feature, index) => (
                  <FeatureCard key={index}>
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
                    <FeatureDescription>{t(feature.descKey)}</FeatureDescription>
                  </FeatureCard>
                ))}
              </FeatureGrid>
            </CategorySection>
            
            <CategorySection>
              <CategoryTitle>{t('featuresPage.marketing.title')}</CategoryTitle>
              <p>{t('featuresPage.marketing.description')}</p>
              
              <FeatureGrid>
                {marketingFeatures.map((feature, index) => (
                  <FeatureCard key={index}>
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
                    <FeatureDescription>{t(feature.descKey)}</FeatureDescription>
                  </FeatureCard>
                ))}
              </FeatureGrid>
            </CategorySection>
          </ContentSection>
        </div>
      </FeaturesSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('featuresPage.cta.title')}</CTATitle>
          <CTAText>{t('featuresPage.cta.description')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t('featuresPage.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default FeaturesPage;